import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DEFAULT_LANG } from "@/i18n";
import type { ToolAdmin } from "@/types/tool";

interface ToolSheetProps {
  isOpen: boolean;
  onClose: () => void;
  tool: ToolAdmin | null;
  onSuccess: () => void;
}

const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'Français' },
  { code: 'nl', name: 'Nederlands' },
];

export const ToolSheet = ({ isOpen, onClose, tool, onSuccess }: ToolSheetProps) => {
  const isEdit = !!tool?.id;
  const [activeTab, setActiveTab] = useState<"edit" | "view">("edit");
  const [form, setForm] = useState<Omit<ToolAdmin, "id" | "updated_at">>({
    name: "",
    description: "",
    icon: "",
    is_active: true,
    is_featured: false,
    is_coming_soon: false,
    category_id: null,
    tool_type_id: null,
    name_i18n: {},
    description_i18n: {},
    created_at: new Date().toISOString(),
    upvotes_count: 0,
    order_index: 0,
  } as any);

  const { data: categories } = useQuery({
    queryKey: ["tool-sheet-categories"],
    queryFn: async () => {
      const { data } = await supabase.from("categories").select("id, name").order("name");
      return data || [];
    },
  });

  const { data: types } = useQuery({
    queryKey: ["tool-sheet-types"],
    queryFn: async () => {
      const { data } = await supabase.from("tool_types").select("id, name").order("name");
      return data || [];
    },
  });

  useEffect(() => {
    if (tool) {
      const { id, updated_at, ...rest } = tool;
      setForm({
        ...rest,
        description: rest.description || "",
        icon: rest.icon || "",
        name_i18n: rest.name_i18n || {},
        description_i18n: rest.description_i18n || {},
        created_at: rest.created_at || new Date().toISOString(),
      } as any);
    } else {
      setForm({
        name: "",
        description: "",
        icon: "",
        is_active: true,
        is_featured: false,
        is_coming_soon: false,
        category_id: null,
        tool_type_id: null,
        name_i18n: {},
        description_i18n: {},
        created_at: new Date().toISOString(),
        upvotes_count: 0,
        order_index: 0,
      } as any);
    }
  }, [tool]);

  const handleSubmit = async () => {
    const payload = {
      name: form.name.trim(),
      description: form.description?.trim() || null,
      icon: form.icon?.trim() || null,
      is_active: !!form.is_active,
      is_featured: !!form.is_featured,
      is_coming_soon: !!form.is_coming_soon,
      category_id: form.category_id || null,
      tool_type_id: form.tool_type_id || null,
      name_i18n: form.name_i18n || {},
      description_i18n: form.description_i18n || {},
      created_at: form.created_at,
      slug: form.slug || '',
      order_index: form.order_index || 0,
    };

    if (!payload.name) return;

    if (isEdit && tool) {
      const { error } = await supabase.from("tools").update(payload).eq("id", tool.id);
      if (error) {
        console.error(error);
        return;
      }
    } else {
      const { error } = await supabase.from("tools").insert(payload);
      if (error) {
        console.error(error);
        return;
      }
    }

    onSuccess();
  };

  const updateI18nField = (field: 'name_i18n' | 'description_i18n', lang: string, value: string) => {
    setForm(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [lang]: value
      }
    }));
  };

  const getI18nValue = (field: 'name_i18n' | 'description_i18n', lang: string) => {
    return (form as any)[field]?.[lang] || '';
  };

  const formatDateForInput = (dateString: string) => {
    const date = new Date(dateString);
    return date.toISOString().slice(0, 16); // Format: YYYY-MM-DDTHH:MM
  };

  if (!isOpen) return null;

  return (
    <Sheet open={isOpen} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{isEdit ? "Edit Tool" : "Add Tool"}</SheetTitle>
        </SheetHeader>
        
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="edit">Edit</TabsTrigger>
            <TabsTrigger value="view">View</TabsTrigger>
          </TabsList>

          <TabsContent value="edit" className="space-y-4 mt-4">
            <div className="space-y-4">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Basic Information</h3>
                
                <div className="grid gap-2">
                  <Label htmlFor="name">Name (English)</Label>
                  <Input 
                    id="name" 
                    value={form.name} 
                    onChange={(e) => setForm({ ...form, name: e.target.value })} 
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="description">Description (English)</Label>
                  <Textarea 
                    id="description" 
                    value={form.description || ""} 
                    onChange={(e) => setForm({ ...form, description: e.target.value })} 
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="icon">Icon (name or emoji)</Label>
                  <Input 
                    id="icon" 
                    value={form.icon || ""} 
                    onChange={(e) => setForm({ ...form, icon: e.target.value })} 
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="order_index">Order Index</Label>
                  <Input 
                    id="order_index" 
                    type="number"
                    value={form.order_index || 0}
                    onChange={(e) => setForm({ ...form, order_index: parseInt(e.target.value) || 0 })} 
                  />
                  <p className="text-xs text-muted-foreground">
                    Lower numbers appear first. Coming soon tools always appear last regardless of order.
                  </p>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="created_at">Created At</Label>
                  <Input 
                    id="created_at" 
                    type="datetime-local"
                    value={formatDateForInput(form.created_at)}
                    onChange={(e) => setForm({ ...form, created_at: new Date(e.target.value).toISOString() })} 
                  />
                  <p className="text-xs text-muted-foreground">
                    You can modify the creation date of the tool
                  </p>
                </div>
              </div>

              {/* Multilanguage Fields */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Multilanguage Fields</h3>
                
                {SUPPORTED_LANGUAGES.filter(lang => lang.code !== 'en').map((lang) => (
                  <div key={lang.code} className="space-y-2 border rounded-lg p-3">
                    <Label className="text-sm font-medium">{lang.name}</Label>
                    
                    <div className="grid gap-2">
                      <Label htmlFor={`name-${lang.code}`} className="text-xs">Name</Label>
                      <Input 
                        id={`name-${lang.code}`}
                        value={getI18nValue('name_i18n', lang.code)}
                        onChange={(e) => updateI18nField('name_i18n', lang.code, e.target.value)}
                        placeholder={`Name in ${lang.name}`}
                      />
                    </div>
                    
                    <div className="grid gap-2">
                      <Label htmlFor={`desc-${lang.code}`} className="text-xs">Description</Label>
                      <Textarea 
                        id={`desc-${lang.code}`}
                        value={getI18nValue('description_i18n', lang.code)}
                        onChange={(e) => updateI18nField('description_i18n', lang.code, e.target.value)}
                        placeholder={`Description in ${lang.name}`}
                        rows={3}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Categories and Types */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Classification</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="grid gap-2">
                    <Label>Category</Label>
                    <Select value={form.category_id || ""} onValueChange={(v) => setForm({ ...form, category_id: v || null })}>
                      <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent>
                        {categories?.map((c: any) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label>Type</Label>
                    <Select value={form.tool_type_id || ""} onValueChange={(v) => setForm({ ...form, tool_type_id: v || null })}>
                      <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>
                        {types?.map((t: any) => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Flags */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Settings</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="flex items-center justify-between border rounded-md px-3 py-2">
                    <Label htmlFor="active" className="cursor-pointer">Active</Label>
                    <Switch id="active" checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                  </div>
                  <div className="flex items-center justify-between border rounded-md px-3 py-2">
                    <Label htmlFor="featured" className="cursor-pointer">Featured</Label>
                    <Switch id="featured" checked={form.is_featured} onCheckedChange={(v) => setForm({ ...form, is_featured: v })} />
                  </div>
                  <div className="flex items-center justify-between border rounded-md px-3 py-2">
                    <Label htmlFor="coming" className="cursor-pointer">Coming soon</Label>
                    <Switch id="coming" checked={form.is_coming_soon} onCheckedChange={(v) => setForm({ ...form, is_coming_soon: v })} />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4 border-t">
                <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
                <Button onClick={handleSubmit} className="flex-1">{isEdit ? "Update" : "Create"}</Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="view" className="mt-4">
            {tool && (
              <div className="space-y-4">
                <div>
                  <div className="text-xl font-semibold flex items-center gap-2">
                    {tool.icon && <span className="text-muted-foreground">{tool.icon}</span>}
                    {tool.name}
                  </div>
                  {tool.description && (
                    <p className="text-sm text-muted-foreground mt-1">{tool.description}</p>
                  )}
                </div>
                
                <div className="flex gap-2 flex-wrap">
                  <Badge variant="outline">{tool.categories?.name || "Uncategorized"}</Badge>
                  <Badge variant="secondary">{tool.tool_types?.name || "—"}</Badge>
                  <Badge variant={tool.is_active ? "default" : "secondary"}>
                    {tool.is_active ? "Active" : "Inactive"}
                  </Badge>
                  {tool.is_featured && <Badge>Featured</Badge>}
                  {tool.is_coming_soon && <Badge variant="outline">Coming soon</Badge>}
                </div>

                {/* Multilanguage Preview */}
                {(tool.name_i18n || tool.description_i18n) && (
                  <div className="space-y-3">
                    <h4 className="font-medium">Multilanguage Content</h4>
                    {SUPPORTED_LANGUAGES.map((lang) => {
                      const name = (tool as any).name_i18n?.[lang.code] || tool.name;
                      const description = (tool as any).description_i18n?.[lang.code] || tool.description;
                      
                      if (lang.code === 'en' || (!name && !description)) return null;
                      
                      return (
                        <div key={lang.code} className="border rounded-lg p-3">
                          <div className="text-sm font-medium text-muted-foreground mb-2">{lang.name}</div>
                          {name && <div className="font-medium">{name}</div>}
                          {description && <div className="text-sm text-muted-foreground mt-1">{description}</div>}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="text-xs text-muted-foreground">
                  Created: {new Date(tool.created_at).toLocaleString()}<br />
                  Updated: {new Date(tool.updated_at).toLocaleString()}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
};